import { checklistsApi } from 'app/services/api';
import _ from 'lodash';
import { normalize, schema } from 'normalizr';

import {
  ChecklistExecutionSnapshotTargetType,
  ChecklistExecutionTargetType,
  ChecklistType,
} from 'constants/checklists';
import { FeatureFlag } from 'constants/featureFlags';
import { LOAN_APPLICATION_ACTION_NAME_PREFIX } from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import {
  mapFlatChecklistResultsToChecklistResultModels,
  mapFlatChecklistResultsToLoanSnapshots,
} from 'utils/checklistFlatStoreAdapter';
import { checkIsGuid, getConditionalObject } from 'utils/data';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { selectFeatureFlagByKey } from 'store/global/selectors';

export const CHECKLISTS_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/checklists`,
  {
    GET_CHECKLISTS: 'GET_CHECKLISTS',
    GET_CHECKLIST: 'GET_CHECKLIST',
    MANAGE_CHECKLIST_RESULT_CHANGE: 'MANAGE_CHECKLIST_RESULT_CHANGE',
    SET_CURRENT_CHECKLIST_ID: 'SET_CURRENT_CHECKLIST_ID',
    TRIGGER_OPTIONAL_CHECKLISTS_RECALCULATION: 'TRIGGER_OPTIONAL_CHECKLISTS_RECALCULATION',
  },
);

const checklistsSchema = new schema.Entity(
  'data',
  {},
  {
    idAttribute: ({ checklist }) => checklist.id,
  },
);

const checklistSchema = new schema.Entity(
  'data',
  {},
  {
    idAttribute: ({ target }) => target.checklistId,
    mergeStrategy: (firstOpt, secondOpt) => {
      const { initialLoan, withPendingChanges } = [firstOpt, secondOpt].reduce(
        (acc, opt) => {
          switch (_.camelCase(opt.type)) {
            case ChecklistType.SAVED:
              acc.initialLoan = opt;
              break;

            case ChecklistType.PENDING_CHANGES:
              acc.withPendingChanges = opt;
              break;

            default:
              break;
          }

          return acc;
        },
        {
          initialLoan: null,
          withPendingChanges: null,
        },
      );

      return {
        ...initialLoan,
        withPendingChanges,
      };
    },
    processStrategy: ({ target, status, checklist }) => ({
      ...status.result,
      type: target.executionType,
      statusType: status.type,
      ...getConditionalObject(!status.result, { checklist }),
    }),
  },
);

// FH-1931/FH-2728: behind `ReadChecklistsFromFlatStore`, fetches the FULL single-checklist read (the
// Alerts tab table, FieldCompletenessIndicator's refetch, and the SignalR per-open-checklist refresh)
// from the flat store directly, instead of the SkynetAPI legacy snapshots proxy. The FULL filter shape
// names BOTH lanes explicitly via `targets` (vs the mount read's broader `targetEntityIds` filter),
// since this read always wants exactly one checklist with Saved + PendingChanges side by side.
const getChecklistSnapshotsData = async (params, isFlatStoreReadEnabled) => {
  if (isFlatStoreReadEnabled) {
    const { applicationId, checklistId } = params;
    const target = executionType => ({
      targetId: applicationId,
      targetType: ChecklistExecutionSnapshotTargetType.LOAN_APPLICATION,
      checklistId,
      executionType,
    });

    const { data } = await checklistsApi.getFlatChecklistResults({
      targets: [
        target(ChecklistExecutionTargetType.SAVED),
        target(ChecklistExecutionTargetType.PENDING_CHANGES),
      ],
      withResults: true,
      withItems: true,
    });

    return mapFlatChecklistResultsToLoanSnapshots(data);
  }

  const { data: responseData } = await checklistsApi.getChecklistsExecutionSnapshots(params);

  return responseData.data;
};

export const getApplicationChecklistByIdAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    // subtab is the checklistId, but it also carries non-GUID sentinels: undefined on the Alerts tab's
    // first mount (before the redirect to a real subtab), and PENDING_CHANGES_SUBTAB ('pending-changes')
    // when the loan-header "view pending changes" drawer is open. A per-checklist read with a non-GUID id
    // is a wasteful no-op — and the flat v2 endpoint rejects such a `targets` entry ("Invalid GUID value").
    // Skip it until a real checklist GUID is selected.
    if (!checkIsGuid(params.checklistId)) {
      return;
    }

    const isFlatStoreReadEnabled = selectFeatureFlagByKey(FeatureFlag.ReadChecklistsFromFlatStore)(
      getState(),
    );
    const data = await getChecklistSnapshotsData(params, isFlatStoreReadEnabled);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.GET_CHECKLIST,
      payload: {
        requestedChecklistIds: _.castArray(params.checklistId || []),
        checklists: normalize(data, [checklistSchema]).entities.data,
      },
    });
  },
);

// FH-3032: behind `ReadChecklistsFromFlatStore`, reads the FH-1829 flat-store lean endpoint instead
// of the SkynetAPI legacy `checklists/result/{applicationId}` proxy. `checklistsSchema` has no
// mergeStrategy (unlike `checklistSchema` above) — it expects exactly one row per checklist, the
// legacy single-lane `ChecklistResultModel` shape. The broad `targetEntityIds` filter returns BOTH
// lanes for every checklist, so the Saved lane is picked explicitly here; its items already carry
// the pending-change overlay fields (`resultWithoutPendingOverrides`/`pendingChangeStatus`) the
// formatted-values selector reads, so no separate PendingChanges-lane fetch/merge is needed.
const getApplicationChecklistsData = async (params, isFlatStoreReadEnabled) => {
  if (isFlatStoreReadEnabled) {
    const { data } = await checklistsApi.getFlatChecklistResults({
      targetEntityIds: [params.applicationId],
      targetEntityType: ChecklistExecutionSnapshotTargetType.LOAN_APPLICATION,
      withResults: true,
      withItems: true,
      // The loan-editor mount read: visible checklists come back head-only (items load on click).
      // Loans no longer infers this from the missing `targets`, so it must be asked for.
      mountRead: true,
    });

    const savedResults = data.filter(
      result => result.executionType === ChecklistExecutionTargetType.SAVED,
    );

    return mapFlatChecklistResultsToChecklistResultModels(savedResults);
  }

  const { data: responseData } = await checklistsApi.getApplicationChecklistsResult(params);

  return responseData.data;
};

export const getApplicationChecklistsAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const isFlatStoreReadEnabled = selectFeatureFlagByKey(FeatureFlag.ReadChecklistsFromFlatStore)(
      getState(),
    );
    const data = await getApplicationChecklistsData(params, isFlatStoreReadEnabled);

    return dispatch({
      type: CHECKLISTS_ACTION_TYPE.GET_CHECKLISTS,
      payload: normalize(data, [checklistsSchema]).entities.data,
    });
  },
);

const manageChecklistResultChangeAction = data => ({
  type: CHECKLISTS_ACTION_TYPE.MANAGE_CHECKLIST_RESULT_CHANGE,
  payload: data,
});

export const changeApplicationChecklistResultAction = catchAsyncActionError(
  params => async dispatch => {
    const { data: responseData } = await checklistsApi.changeApplicationChecklistResult(params);
    const { data } = responseData;

    dispatch(manageChecklistResultChangeAction(responseData));

    return data;
  },
);

export const setCurrentChecklistIdAction = checklistId => ({
  type: CHECKLISTS_ACTION_TYPE.SET_CURRENT_CHECKLIST_ID,
  payload: checklistId,
});
